import React, { useEffect, useMemo, useState } from "react";
import * as AppConstants from "AppConstants";
import { checkUserHasAdminRole, readSessionCurrentClient } from "app/services/app.state.service";
import { Card, Grid } from "@mui/material";
import { HolidayListView } from "app/components/leave-plans/HolidayListView";
import { ActivityStatsDataGridBase } from "app/components/dashboards/ActivityStatsDataGridBase";
import { loadUnifiedEffortsMonthly } from "app/services/report.service";
import { loadAllManagersFromBackend } from "app/services/manager.service";
import { getDateInDDMMYYYYFormat } from "utils/dateUtil";
import { toClientListOptions } from "utils/clientOptions";
import { resolveOwnerParams } from "utils/effortOwnerParams";
import { EffortFilterBar } from "app/components/common/EffortFilterBar";
import { defaultPeriod, periodRange } from "app/components/common/PeriodFilter";
import { useUserFilterOptions } from "app/hooks/useUserFilterOptions";
import { handleAxiosError } from "app/services/app.error.handler";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { ActivityStatsChartsRow } from "app/components/dashboards/ActivityStatsChartsRow";
import { LeavePlanSummaryCard } from "app/components/leave-plans/LeavePlanSummaryCard";
import { ConsolidatedReportFilter } from "../reports/ConsolidatedReportFilter";
import { useDispatch, useSelector } from "react-redux";
import { memberActions } from "store/member-slice";
import { loadMembersForAllClientsFromBackend } from "app/services/member.service";
import EffortSummaryView from "../effortSummary/EffortSummaryView";
import { NoDataBanner } from "app/components/dashboards/NoDataBanner";
import "./dashboards.css";

export const AdminDashboard = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const members = useSelector((state) => state.member.rows);

  const [clients, setClients] = useState([]);
  const [managers, setManagers] = useState([]);

  // Pending (edited in the filter row) vs applied. Only the range is still held after Apply — it
  // labels the card. The client and user now travel into the query rather than filtering the
  // response, so there is nothing left for the component to remember them for.
  const [pendingClientId, setPendingClientId] = useState(AppConstants.ALL_CLIENTS);
  const [pendingPeriod, setPendingPeriod] = useState(defaultPeriod);
  const [pendingUser, setPendingUser] = useState(AppConstants.ALL_USERS);
  const [appliedRange, setAppliedRange] = useState(() => periodRange(defaultPeriod()));
  const [allRows, setAllRows] = useState([]);

  useEffect(() => {
    const list = readSessionCurrentClient();
    setClients(Array.isArray(list) ? list : []);
  }, []);

  useEffect(() => {
    if (members.length === 0) {
      loadMembersForAllClientsFromBackend()
        .then((response) => dispatch(memberActions.replaceData(response?.data)))
        .catch((error) => {
          dispatch(memberActions.replaceData([]));
          handleAxiosError(error);
        });
    }
  }, [dispatch, members.length]);

  useEffect(() => {
    loadAllManagersFromBackend()
      .then((r) => setManagers(r.status === 204 || !Array.isArray(r.data) ? [] : r.data))
      .catch(() => setManagers([]));
  }, []);

  const clientOptions = toClientListOptions(clients);

  // Reset the user to "All" whenever the pending client changes (a person from the
  // previous client shouldn't stay selected against a client they don't belong to).
  const onClientChange = (val) => {
    setPendingClientId(val);
    setPendingUser(AppConstants.ALL_USERS);
  };

  // Dropdown options track the currently-SELECTED (pending) client so the User list
  // reflects the client being chosen before Apply (mirrors the Consolidated Report).
  const optionMembers = useMemo(
    () =>
      pendingClientId === AppConstants.ALL_CLIENTS
        ? members
        : members.filter((m) => String(m.client?.id ?? m.clientId) === String(pendingClientId)),
    [members, pendingClientId]
  );
  const optionManagers = useMemo(
    () =>
      pendingClientId === AppConstants.ALL_CLIENTS
        ? managers
        : managers.filter((m) => String(m.clientId) === String(pendingClientId)),
    [managers, pendingClientId]
  );
  const { memberOptions, managerOptions } = useUserFilterOptions({
    members: optionMembers,
    managers: optionManagers,
  });

  const clientLabelById = useMemo(() => {
    const map = {};
    clients.forEach((c) => {
      map[String(c.id)] = c.name ?? c.code ?? String(c.id);
    });
    return map;
  }, [clients]);

  // One request, every filter applied in SQL. Previously this fanned out one call per client and
  // pulled every member's rows into the browser so the User dropdown could filter them here; the
  // client list and the selected person are now both part of the query.
  const apply = React.useCallback(
    async (clientId, range, user) => {
      setIsLoading(true);
      const targetClientIds =
        clientId === AppConstants.ALL_CLIENTS ? clients.map((c) => String(c.id)) : [String(clientId)];
      try {
        const response = await loadUnifiedEffortsMonthly({
          fromDate: range.from,
          toDate: range.to,
          clientIds: targetClientIds,
          ...resolveOwnerParams(user, members, [], managers),
        });
        setAllRows(response.status === 204 || !Array.isArray(response.data) ? [] : response.data);
      } catch (error) {
        handleAxiosError(error);
        setAllRows([]);
      } finally {
        setIsLoading(false);
      }
    },
    [clients, members, managers]
  );

  // Initial auto-load once the client list is known (defaults: All clients, current month).
  const didInit = React.useRef(false);
  useEffect(() => {
    if (didInit.current) return;
    if (!checkUserHasAdminRole()) return;
    if (clients.length === 0) return;
    didInit.current = true;
    apply(AppConstants.ALL_CLIENTS, appliedRange, AppConstants.ALL_USERS);
  }, [clients, apply, appliedRange]);

  const onApply = () => {
    const range = periodRange(pendingPeriod);
    setAppliedRange(range);
    apply(pendingClientId, range, pendingUser);
  };

  // The query is already scoped to the applied client and user, so what comes back is what shows.
  const displayRows = allRows;

  if (!checkUserHasAdminRole()) {
    return <div>You do not have access to this functionality.</div>;
  }

  return (
    <div className="content-container dashboard-container">
      <Grid container rowSpacing={10} columnSpacing={0}>
        <Grid size={12}>
          <Card>
            <MDBox mx={2} mt={-3} py={1} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
              <MDTypography variant="h6" color="white">
                {`Efforts summary for period - ${getDateInDDMMYYYYFormat(appliedRange.from)} and ${getDateInDDMMYYYYFormat(
                  appliedRange.to
                )}`}
              </MDTypography>
            </MDBox>
            <MDBox px={2} pt={3}>
              <EffortFilterBar
                idPrefix="admin-effort"
                showClient
                clientValue={pendingClientId}
                onClientChange={onClientChange}
                clientOptions={clientOptions}
                showMemberManagerDropdown
                userValue={pendingUser}
                onUserChange={setPendingUser}
                memberOptions={memberOptions}
                managerOptions={managerOptions}
                period={pendingPeriod}
                onPeriodChange={setPendingPeriod}
                onAction={onApply}
              />
            </MDBox>
            {isLoading ? (
              <NoDataBanner message="Loading efforts summary..." />
            ) : displayRows.length === 0 ? (
              <NoDataBanner />
            ) : (
              <>
                <ActivityStatsChartsRow
                  activityStatsData={displayRows}
                  fromDate={appliedRange.from}
                  toDate={appliedRange.to}
                />
                <MDBox mt={2}>
                  <ActivityStatsDataGridBase activityStatsData={displayRows} clientLabelById={clientLabelById} />
                </MDBox>
              </>
            )}
          </Card>
        </Grid>
        <Grid size={12}>
          <EffortSummaryView />
        </Grid>
        <Grid size={12}>
          <ConsolidatedReportFilter role={AppConstants.ROLE_ADMIN} members={members} />
        </Grid>
        <Grid size={12}>
          {/* Scope travels as filter parameters, so the admin view names its clients explicitly
              rather than relying on an endpoint that returned everything. */}
          <LeavePlanSummaryCard
            idPrefix="admin-leave"
            role={AppConstants.ROLE_ADMIN}
            context={{ clientIds: clients.map((c) => c.id) }}
            showClient
            clientOptions={clientOptions}
            showUser
            members={members}
            managers={managers}
          />
        </Grid>
        <Grid size={12}>
          <HolidayListView />
        </Grid>
      </Grid>
    </div>
  );
};
